import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { AlertTriangle } from "lucide-react";
import api from "../lib/api";
import { fmtNum, fmtMoney, pnlColor } from "../lib/format";
import DataTable from "../components/DataTable";
import StatusPill from "../components/StatusPill";
import EntityFormDialog from "../components/EntityFormDialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";

export default function Positions() {
  const { data: physical = [] } = useQuery({ queryKey: ["phys"], queryFn: () => api.get("/positions/physical") });
  const { data: net = [] } = useQuery({ queryKey: ["net-exp"], queryFn: () => api.get("/positions/net-exposure") });
  const { data: hedges = [] } = useQuery({ queryKey: ["hedges"], queryFn: () => api.get("/hedge-positions") });
  const [hedgeForm, setHedgeForm] = useState(false);

  const hedgeFields = [
    { key: "commodity", label: "Commodity", type: "select", options: ["CPO", "RBD Palm Olein", "Crude Soybean Oil", "Soybeans", "Sunflower Oil"], required: true },
    { key: "exchange", label: "Exchange", type: "select", options: ["BMD", "CBOT", "MCX", "NCDEX", "ICE", "DCE"], required: true },
    { key: "contract_month", label: "Contract Month", type: "text", default: "Aug-26", required: true },
    { key: "side", label: "Side", type: "select", options: ["Long", "Short"], default: "Short", required: true },
    { key: "lots", label: "Lots", type: "number", required: true },
    { key: "lot_size", label: "Lot Size (MT)", type: "number", default: 25 },
    { key: "entry_price", label: "Entry Price", type: "number", required: true },
    { key: "mtm_price", label: "MTM Price", type: "number", required: true },
    { key: "status", label: "Status", type: "select", options: ["Open", "Closed"], default: "Open" },
    { key: "realised_pnl", label: "Realised P&L", type: "number", default: 0 },
    { key: "margin_posted", label: "Margin Posted", type: "number", default: 0 },
    { key: "currency", label: "Currency", type: "select", options: ["USD", "MYR", "INR"], default: "USD" },
  ];

  const physCols = [
    { key: "commodity", label: "Commodity", render: (r) => <span className="font-semibold uppercase text-xs">{r.commodity}</span> },
    { key: "delivery_month", label: "Delivery Month" },
    { key: "origin", label: "Origin" },
    { key: "bought", label: "Bought (MT)", align: "right", render: (r) => fmtNum(r.bought) },
    { key: "sold", label: "Sold (MT)", align: "right", render: (r) => fmtNum(r.sold) },
    { key: "net_open", label: "Net Open (MT)", align: "right", render: (r) => <span className={pnlColor(r.net_open) + " font-semibold"}>{fmtNum(r.net_open)}</span> },
    { key: "status", label: "Position", render: (r) => <StatusPill status={r.status === "Covered" ? "Closed" : r.status === "Long" ? "Active" : "Draft"} /> },
  ];

  const hedgeCols = [
    { key: "commodity", label: "Commodity", render: (r) => <span className="font-semibold uppercase text-xs">{r.commodity}</span> },
    { key: "exchange", label: "Exchange" },
    { key: "contract_month", label: "Month" },
    { key: "side", label: "Side", render: (r) => <span className={r.side === "Long" ? "text-emerald-600" : "text-rose-600"}>{r.side}</span> },
    { key: "lots", label: "Lots", align: "right", render: (r) => fmtNum(r.lots) },
    { key: "entry_price", label: "Entry", align: "right", render: (r) => fmtNum(r.entry_price, 2) },
    { key: "mtm_price", label: "MTM", align: "right", render: (r) => fmtNum(r.mtm_price, 2) },
    { key: "margin_posted", label: "Margin", align: "right", render: (r) => fmtMoney(r.margin_posted) },
    { key: "mtm_pnl", label: "MTM P&L", align: "right", render: (r) => {
        const p = r.status === "Closed" ? r.realised_pnl : (r.mtm_price - r.entry_price) * r.lots * r.lot_size * (r.side === "Long" ? 1 : -1);
        return <span className={pnlColor(p) + " font-semibold"}>{fmtMoney(p)}</span>;
      } },
    { key: "status", label: "Status", render: (r) => <StatusPill status={r.status} /> },
  ];

  return (
    <div className="space-y-5" data-testid="positions-page">
      <div>
        <h3 className="text-base font-bold mb-3">Net Exposure by Commodity (Physical + Derivative)</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {net.map((e) => (
            <div key={e.commodity} data-testid={`exposure-${e.commodity}`}
              className={`bg-white dark:bg-slate-900 border rounded-lg p-4 shadow-sm ${e.breach ? "border-rose-400 dark:border-rose-800" : "border-slate-200 dark:border-slate-800"}`}>
              <div className="flex justify-between items-start">
                <p className="font-semibold uppercase text-sm">{e.commodity}</p>
                {e.breach && <span className="flex items-center gap-1 text-xs text-rose-600 font-semibold"><AlertTriangle className="h-3.5 w-3.5" /> BREACH</span>}
              </div>
              <div className="mt-2 grid grid-cols-3 gap-2 text-center">
                <div><p className="text-[10px] text-slate-400">Physical</p><p className={`font-mono text-sm ${pnlColor(e.physical_net)}`}>{fmtNum(e.physical_net)}</p></div>
                <div><p className="text-[10px] text-slate-400">Hedge</p><p className={`font-mono text-sm ${pnlColor(e.hedge_net)}`}>{fmtNum(e.hedge_net)}</p></div>
                <div><p className="text-[10px] text-slate-400">Net (MT)</p><p className={`font-mono text-sm font-bold ${pnlColor(e.net_exposure)}`}>{fmtNum(e.net_exposure)}</p></div>
              </div>
              <div className="mt-2 h-1.5 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                <div className={`h-full ${e.breach ? "bg-rose-500" : "bg-orange-500"}`} style={{ width: `${Math.min(Math.abs(e.net_exposure) / e.risk_limit * 100, 100)}%` }} />
              </div>
              <p className="text-[10px] text-slate-400 mt-1">Risk limit: {fmtNum(e.risk_limit)} MT</p>
            </div>
          ))}
        </div>
      </div>

      <Tabs defaultValue="physical">
        <TabsList className="mb-3">
          <TabsTrigger value="physical" data-testid="tab-physical">Physical Book</TabsTrigger>
          <TabsTrigger value="fno" data-testid="tab-fno">F&O Hedge Book</TabsTrigger>
        </TabsList>
        <TabsContent value="physical">
          <DataTable testid="physical-table" title="Physical Position Book" columns={physCols} rows={physical} csvName="physical_positions" />
        </TabsContent>
        <TabsContent value="fno">
          <DataTable testid="hedge-table" title="F&O Position Book" columns={hedgeCols} rows={hedges} csvName="hedge_positions"
            onAdd={() => setHedgeForm(true)} addLabel="New Hedge" />
        </TabsContent>
      </Tabs>
      <EntityFormDialog open={hedgeForm} onClose={() => setHedgeForm(false)} title="New F&O Hedge Position"
        path="hedge-positions" fields={hedgeFields} testid="hedge-form" />
    </div>
  );
}
